import { useEffect, useState, useCallback } from 'react';
import { ACTION_TYPE_LABELS } from '../../constants';

export default function AdminCreditPanel({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [grantAmount, setGrantAmount] = useState('1000');
  const [grantReason, setGrantReason] = useState('Admin grant');
  const [isProcessing, setIsProcessing] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [panelTab, setPanelTab] = useState('users');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [statsTrigger, setStatsTrigger] = useState(0);

  const handleRefresh = useCallback(() => {
    setLoading(true);
    setRefreshTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (panelTab !== 'stats' || !user) return;
    let isMounted = true;

    async function loadStats() {
      if (isMounted) { setStatsLoading(true); setStatsError(null); }
      try {
        const idToken = await user.getIdToken();
        const resp = await fetch('/api/v1/admin/usage-stats', {
          headers: { 'Authorization': `Bearer ${idToken}` }
        });
        if (!resp.ok) throw new Error(`Failed: ${resp.status}`);
        const data = await resp.json();
        if (isMounted) setStats(data);
      } catch (err) {
        if (isMounted) setStatsError(err.message);
      } finally {
        if (isMounted) setStatsLoading(false);
      }
    }

    loadStats();
    return () => { isMounted = false; };
  }, [panelTab, user, statsTrigger]);

  useEffect(() => {
    let isMounted = true;

    async function performFetch() {
      if (!user) {
        if (isMounted) {
          setUsers([]);
          setLoading(false);
        }
        return;
      }

      try {
        const idToken = await user.getIdToken();
        const resp = await fetch('/api/v1/admin/users', {
          headers: { 'Authorization': `Bearer ${idToken}` }
        });
        if (!resp.ok) throw new Error(`Failed to fetch users: ${resp.status}`);
        const data = await resp.json();
        if (isMounted) {
          setUsers(data.users || []);
          setError(null);
        }
      } catch (err) {
        if (isMounted) setError(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    performFetch();
    return () => { isMounted = false; };
  }, [user, refreshTrigger]);

  const handleGrant = async (e) => {
    e.preventDefault();
    if (!selectedUser || isProcessing || !user) return;

    setIsProcessing(true);
    try {
      const idToken = await user.getIdToken();
      const resp = await fetch(`/api/v1/admin/users/${selectedUser.uid}/grant`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseInt(grantAmount, 10),
          reason: grantReason,
          role: selectedUser.role || 'credit_beta'
        })
      });

      if (!resp.ok) {
        const errData = await resp.json();
        throw new Error(errData.error?.message || 'Grant failed');
      }

      handleRefresh();
      setIsGrantModalOpen(false);
      setSelectedUser(null);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = async (targetUser) => {
    if (!user || !window.confirm(`Reset credits for ${targetUser.email || targetUser.uid}?`)) return;

    try {
      const idToken = await user.getIdToken();
      const resp = await fetch(`/api/v1/admin/users/${targetUser.uid}/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ resetAt: new Date().toISOString() })
      });

      if (!resp.ok) {
        const errData = await resp.json();
        throw new Error(errData.error?.message || 'Reset failed');
      }

      handleRefresh();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  if (loading && users.length === 0) return <div className="p-4 text-gray-400">Loading user list...</div>;

  const sortedUsers = [...users].sort((a, b) => {
    if (a.hasPendingRequest && !b.hasPendingRequest) return -1;
    if (!a.hasPendingRequest && b.hasPendingRequest) return 1;
    return 0;
  });

  // Collect all action types seen in stats
  const allActionTypes = stats
    ? [...new Set(stats.stats.flatMap(s => Object.keys(s.byAction)))]
        .filter(a => a !== 'context_select')
        .sort()
    : [];

  const emailOf = uid => users.find(u => u.uid === uid)?.email || uid.slice(0, 8) + '…';

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-gray-900 overflow-hidden">
      <div className="p-3 border-b border-gray-800 flex justify-between items-center bg-gray-900/50">
        <div className="flex bg-gray-800 rounded-lg p-0.5 border border-gray-700">
          <button
            onClick={() => setPanelTab('users')}
            className={`px-3 py-1 rounded text-xs font-bold transition-all ${panelTab === 'users' ? 'bg-gray-700 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200'}`}
          >
            ユーザー
          </button>
          <button
            onClick={() => setPanelTab('stats')}
            className={`px-3 py-1 rounded text-xs font-bold transition-all ${panelTab === 'stats' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200'}`}
          >
            利用統計
          </button>
        </div>
        {panelTab === 'users' ? (
          <button onClick={handleRefresh} disabled={loading} className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50">
            {loading ? '...' : 'Refresh'}
          </button>
        ) : (
          <button onClick={() => setStatsTrigger(prev => prev + 1)} disabled={statsLoading} className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50">
            {statsLoading ? '...' : 'Refresh'}
          </button>
        )}
      </div>

      {panelTab === 'stats' ? (
        <div className="flex-1 flex flex-col min-h-0 overflow-auto">
          {statsError && <div className="p-3 bg-rose-900/20 text-rose-400 text-xs border-b border-rose-900/30">Error: {statsError}</div>}
          {statsLoading && <div className="p-4 text-gray-400 text-xs">集計中...</div>}
          {stats && !statsLoading && (
            <>
              <div className="px-4 py-2 text-[10px] text-gray-500 border-b border-gray-800">
                直近1000件（context_select除く）の集計 / {stats.eventCount} events
              </div>
              <div className="flex-1 overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead className="bg-gray-800 text-gray-400 sticky top-0">
                    <tr>
                      <th className="p-3 border-b border-gray-700 whitespace-nowrap">ユーザー</th>
                      {allActionTypes.map(a => (
                        <th key={a} className="p-3 border-b border-gray-700 whitespace-nowrap text-right">
                          {ACTION_TYPE_LABELS[a] || a}
                        </th>
                      ))}
                      <th className="p-3 border-b border-gray-700 text-right whitespace-nowrap">合計</th>
                      <th className="p-3 border-b border-gray-700 text-right whitespace-nowrap">件数</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {stats.stats.sort((a, b) => b.total - a.total).map(row => (
                      <tr key={row.ownerUid} className="hover:bg-gray-800/40">
                        <td className="p-3 text-gray-300 font-mono text-[10px]">{emailOf(row.ownerUid)}</td>
                        {allActionTypes.map(a => (
                          <td key={a} className="p-3 text-right text-gray-400 font-mono">
                            {row.byAction[a] ? Math.round(row.byAction[a] * 10) / 10 : '—'}
                          </td>
                        ))}
                        <td className="p-3 text-right font-mono font-bold text-white">{Math.round(row.total * 10) / 10}</td>
                        <td className="p-3 text-right font-mono text-gray-500">{row.count}</td>
                      </tr>
                    ))}
                    {/* Totals row */}
                    {stats.stats.length > 1 && (() => {
                      const grandTotal = stats.stats.reduce((s, r) => s + r.total, 0);
                      const grandCount = stats.stats.reduce((s, r) => s + r.count, 0);
                      return (
                        <tr className="bg-gray-800/60 border-t-2 border-gray-700">
                          <td className="p-3 text-gray-400 font-bold text-[10px]">合計</td>
                          {allActionTypes.map(a => {
                            const sum = stats.stats.reduce((s, r) => s + (r.byAction[a] || 0), 0);
                            return (
                              <td key={a} className="p-3 text-right font-mono text-gray-300">
                                {sum > 0 ? Math.round(sum * 10) / 10 : '—'}
                              </td>
                            );
                          })}
                          <td className="p-3 text-right font-mono font-bold text-indigo-300">{Math.round(grandTotal * 10) / 10}</td>
                          <td className="p-3 text-right font-mono text-gray-400">{grandCount}</td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
      {error && <div className="p-3 bg-rose-900/20 text-rose-400 text-xs border-b border-rose-900/30">Error: {error}</div>}

      <div className="flex-1 overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse">
          <thead className="bg-gray-800 text-gray-400 sticky top-0">
            <tr>
              <th className="p-3 border-b border-gray-700">Email / UID</th>
              <th className="p-3 border-b border-gray-700">Role</th>
              <th className="p-3 border-b border-gray-700">Granted</th>
              <th className="p-3 border-b border-gray-700">Used</th>
              <th className="p-3 border-b border-gray-700">Remaining</th>
              <th className="p-3 border-b border-gray-700">ResetAt</th>
              <th className="p-3 border-b border-gray-700 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {sortedUsers.map((u) => {
              const remaining = Math.max(0, u.grantedCredits - u.usedCredits);
              const resetDate = u.resetAt ? new Date(u.resetAt).toLocaleDateString() : 'N/A';
              return (
                <tr key={u.uid} className="hover:bg-gray-800/40 transition-colors">
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-white">{u.email || 'No email'}</div>
                      {u.hasPendingRequest && (
                        <span className="bg-amber-900/50 text-amber-400 text-[9px] font-bold px-1 py-0.5 rounded border border-amber-800 animate-pulse">
                          申請中
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-500 font-mono">{u.uid}</div>
                  </td>
                  <td className="p-3">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${u.role === 'owner' ? 'bg-rose-900/30 text-rose-400 border border-rose-800/50' : 'bg-blue-900/30 text-blue-400 border border-blue-800/50'}`}>
                      {u.role || 'member'}
                    </span>
                  </td>
                  <td className="p-3 text-gray-300 font-mono">{u.grantedCredits.toLocaleString()}</td>
                  <td className="p-3 text-gray-400 font-mono">{u.usedCredits.toLocaleString()}</td>
                  <td className="p-3">
                    <span className={`font-mono font-bold ${remaining < 100 ? 'text-rose-400' : 'text-emerald-400'}`}>
                      {remaining.toLocaleString()}
                    </span>
                  </td>
                  <td className="p-3 text-gray-500">{resetDate}</td>
                  <td className="p-3 text-right space-x-2 whitespace-nowrap">
                    <button
                      onClick={() => {
                        setSelectedUser(u);
                        setGrantAmount('1000');
                        setGrantReason('Admin grant');
                        setIsGrantModalOpen(true);
                      }}
                      className="text-indigo-400 hover:text-indigo-300 font-bold px-2 py-1 rounded bg-indigo-900/20 border border-indigo-800/50 hover:bg-indigo-900/40"
                    >
                      付与
                    </button>
                    <button
                      onClick={() => handleReset(u)}
                      className="text-gray-400 hover:text-white px-2 py-1 rounded border border-gray-700 hover:bg-gray-800"
                    >
                      リセット
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isGrantModalOpen && selectedUser && (
        <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h4 className="text-sm font-bold text-white">クレジット付与: {selectedUser.email || selectedUser.uid.slice(0, 8)}</h4>
              <button aria-label="閉じる" onClick={() => setIsGrantModalOpen(false)} className="text-gray-400 hover:text-white">×</button>
            </div>
            <form onSubmit={handleGrant} className="p-4 space-y-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">付与量 (Credits)</label>
                <input
                  type="number"
                  value={grantAmount}
                  onChange={(e) => setGrantAmount(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 text-white rounded px-3 py-2 outline-none focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">理由</label>
                <input
                  type="text"
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 text-white rounded px-3 py-2 outline-none focus:border-blue-500"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsGrantModalOpen(false)}
                  className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors text-sm"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-500 transition-colors text-sm font-bold disabled:opacity-50"
                >
                  {isProcessing ? '処理中...' : '付与実行'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
